import React, { useState } from 'react';
import { CheckCircle2, Circle, Clock, XCircle, RotateCcw, Sparkles } from 'lucide-react';

const CONTRACTS_META = [
  { num: 0,    name: 'Research Universe',              color: 'indigo'  },
  // Percorso alternativo — eseguito AL POSTO di C1+C1B
  { num: 0.1,  name: 'Cognitive Prior Transfer',       color: 'purple', isAlternative: true },
  { num: 1,    name: 'Market Research',                color: 'blue'    },
  { num: 1.1,  name: 'Consumer Research Engine',       color: 'blue',   isOptional: true },
  { num: 1.5,  name: 'Click Population Strategy',      color: 'sky'     },
  { num: 2,    name: 'Creative Constraints',           color: 'cyan'    },
  { num: 3,    name: 'Latent State Analysis',          color: 'teal'    },
  { num: 4,    name: 'Policy Perception',              color: 'green'   },
  { num: 5,    name: 'Structural Taxonomy',            color: 'lime'    },
  { num: 6,    name: 'Structure Selection',            color: 'yellow'  },
  { num: 7,    name: 'Observation Sequencing',         color: 'amber'   },
  { num: 8,    name: 'Script Structure Layer',         color: 'orange'  },
  { num: 9,    name: 'Creative Brief Definition',      color: 'red'     },
  { num: 9.5,  name: 'Native Platform Language',       color: 'violet'  },
  { num: 10,   name: 'Environment & Context Design',   color: 'rose'    },
  { num: 11,   name: 'Script Library Production',      color: 'pink'    },
  { num: 12,   name: 'Script Review',                  color: 'fuchsia' },
  { num: 13,   name: 'Avatar Identity',                color: 'purple'  },
  { num: 14.1, name: 'Behavior Generative Engine',     color: 'violet'  },
  { num: 14.2, name: 'Human Motion Compiler',          color: 'indigo'  },
  { num: 15,   name: 'Native UGC Rendering',           color: 'indigo'  },
  { num: 16,   name: 'Video Generation Specification', color: 'blue'    },
  { num: 16.1, name: 'Render IR Compiler',             color: 'cyan'    },
  { num: 16.2, name: 'Renderer Configuration',         color: 'sky'     },
  { num: 16.3, name: 'Renderer Backend Adapter',       color: 'teal'    },
  { num: 16.4, name: 'Video Generation Confirm',       color: 'green'   },
  { num: 17,   name: 'Video Prompt Compiler',          color: 'lime'    },
  { num: 18,   name: 'Video Generation',               color: 'yellow'  },
  { num: 19,   name: 'Structural Validation',          color: 'amber'   },
  { num: 20,   name: 'Perceptual Validation',          color: 'orange'  },
  { num: 21,   name: 'Audio Extraction',               color: 'red'     },
  { num: 22,   name: 'Audio Humanization',             color: 'rose'    },
  { num: 23,   name: 'Audio-Video Merge',              color: 'pink'    },
  { num: 24,   name: 'Video Editing',                  color: 'fuchsia' },
  { num: 25,   name: 'Final QA',                       color: 'purple'  },
  { num: 26,   name: 'Creative State Registry',        color: 'violet'  }
];

// Contract non nella sequenza principale — calcoliamo il progresso solo sui principali
const MAIN_SEQUENCE = CONTRACTS_META.filter(c => !c.isAlternative && !c.isOptional);

const ContractSidebar = ({
  currentContract,
  completedContracts = [],
  onContractSelect,
  onResetFrom,
  className = ''
}) => {
  const [resetting, setResetting] = useState(null);

  const getContractStatus = (contract) => {
    const { num, isAlternative, isOptional } = contract;
    if (completedContracts.includes(num)) return 'completed';
    if (num === currentContract) return 'current';

    // C0X: disponibile solo se C0 è completato e il percorso alternativo è stato scelto
    // (riconoscibile da completedContracts che include 0.1 o currentContract === 0.1)
    if (isAlternative) {
      const c0Done = completedContracts.includes(0);
      const altChosen = completedContracts.includes(0.1) || currentContract === 0.1;
      if (!c0Done) return 'locked';
      return altChosen ? 'available' : 'available'; // sempre cliccabile dopo C0
    }

    // C1B: opzionale, disponibile dopo C1
    if (isOptional) {
      return completedContracts.includes(1) ? 'available' : 'locked';
    }

    if (num < currentContract) return 'available';
    return 'locked';
  };

  const getStatusIcon = (status, isAlternative) => {
    if (isAlternative && status === 'available') {
      return <Sparkles className="w-4 h-4 text-purple-400 shrink-0" />;
    }
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />;
      case 'current':   return <Clock className="w-4 h-4 text-blue-500 animate-pulse shrink-0" />;
      case 'available': return <Circle className="w-4 h-4 text-gray-400 shrink-0" />;
      default:          return <XCircle className="w-4 h-4 text-gray-300 shrink-0" />;
    }
  };

  const getItemStyles = (status, isAlternative, isCurrent) => {
    const base = 'group flex items-center gap-2 px-3 py-2 rounded-lg transition-all cursor-pointer';
    if (isAlternative) {
      if (status === 'completed') return `${base} bg-purple-50 hover:bg-purple-100 border-l-4 border-purple-500`;
      if (isCurrent)              return `${base} bg-purple-50 hover:bg-purple-100 border-l-4 border-purple-500 shadow-sm`;
      if (status === 'locked')    return `${base} bg-gray-50 opacity-40 cursor-not-allowed border-l-4 border-transparent`;
      return `${base} bg-purple-50/50 hover:bg-purple-100/50 border-l-4 border-purple-200`;
    }
    switch (status) {
      case 'completed': return `${base} bg-green-50 hover:bg-green-100 border-l-4 border-green-500`;
      case 'current':   return `${base} bg-blue-50 hover:bg-blue-100 border-l-4 border-blue-500 shadow-sm`;
      case 'available': return `${base} bg-gray-50 hover:bg-gray-100 border-l-4 border-transparent`;
      default:          return `${base} bg-gray-50 opacity-40 cursor-not-allowed border-l-4 border-transparent`;
    }
  };

  const handleReset = async (e, contractNum) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      `Reset dal contract ${contractNum}?\n\nVerranno cancellate tutte le execution dal C${contractNum} in poi. Il progetto ripartirà da quel punto.\n\nQuesta operazione non è reversibile.`
    );
    if (!confirmed) return;
    setResetting(contractNum);
    try {
      await onResetFrom(contractNum);
    } catch (err) {
      alert(`Errore reset: ${err.message}`);
    } finally {
      setResetting(null);
    }
  };

  const completedMain = completedContracts.filter(n =>
    MAIN_SEQUENCE.some(c => c.num === n)
  ).length;

  return (
    <div className={`bg-white border-r border-gray-200 overflow-y-auto ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
        <h2 className="text-base font-semibold text-gray-800">Contracts</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          {completedMain} / {MAIN_SEQUENCE.length} completati
        </p>
        <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-gradient-to-r from-indigo-500 to-pink-500 h-1.5 rounded-full transition-all duration-500"
            style={{ width: `${(completedMain / MAIN_SEQUENCE.length) * 100}%` }}
          />
        </div>
      </div>

      {/* List */}
      <div className="p-2 space-y-0.5">
        {CONTRACTS_META.map((contract) => {
          const status      = getContractStatus(contract);
          const isCurrent   = contract.num === currentContract;
          const isLocked    = status === 'locked';
          const canReset    = !isLocked && onResetFrom && !contract.isAlternative;
          const isResetting = resetting === contract.num;

          // Separatore visivo prima di C1 (inizio percorso standard)
          const showStandardLabel = contract.num === 1;
          // Separatore visivo prima di C0X (percorso alternativo)
          const showAltLabel = contract.num === 0.1;

          return (
            <React.Fragment key={contract.num}>
              {showAltLabel && (
                <div className="px-3 pt-3 pb-1">
                  <p className="text-xs font-semibold text-purple-500 uppercase tracking-wider">
                    Percorso alternativo
                  </p>
                </div>
              )}
              {showStandardLabel && (
                <div className="px-3 pt-3 pb-1">
                  <p className="text-xs font-semibold text-blue-500 uppercase tracking-wider">
                    Percorso standard
                  </p>
                </div>
              )}

              <div
                onClick={() => !isLocked && onContractSelect(contract.num)}
                className={getItemStyles(status, contract.isAlternative, isCurrent)}
              >
                {getStatusIcon(status, contract.isAlternative)}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400 font-mono shrink-0">
                      {contract.isAlternative ? 'C0X' : `C${contract.num.toString().padStart(2, '0')}`}
                    </span>
                    {isCurrent && (
                      <span className={`px-1.5 py-0.5 text-xs ${contract.isAlternative ? 'bg-purple-500' : 'bg-blue-500'} text-white rounded-full leading-none shrink-0`}>
                        Attivo
                      </span>
                    )}
                    {contract.isAlternative && !isCurrent && status !== 'completed' && (
                      <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-600 rounded-full leading-none shrink-0">
                        ALT
                      </span>
                    )}
                    {contract.isOptional && (
                      <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded-full leading-none shrink-0">
                        OPT
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-medium text-gray-800 truncate leading-tight">
                    {contract.name}
                  </p>
                </div>

                {canReset && (
                  <button
                    onClick={(e) => handleReset(e, contract.num)}
                    disabled={isResetting}
                    title="Reset da qui in poi"
                    className="opacity-0 group-hover:opacity-100 shrink-0 p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-50"
                  >
                    <RotateCcw className={`w-3 h-3 ${isResetting ? 'animate-spin' : ''}`} />
                  </button>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default ContractSidebar;
